/* @flow */
import * as React from 'react';
import idx from 'idx';
import { graphql } from 'react-apollo';

import Box from 'components/Box';
import CenteredContainer from 'components/CenteredContainer';
import Container from 'components/Container';
import ErrorReporter from 'app/components/ErrorReporter';
import GroupViewQueryParametersConsumer from 'groups/components/GroupViewQueryParametersConsumer';
import LoadingIndicator from 'components/LoadingIndicator';
import ORGANIZATION_UPSELL_PAGE_QUERY from 'organizations/queries/OrganizationUpsellPageQuery.gql';
import OrganizationAdminUpsell from 'organizations/components/OrganizationAdminUpsell';
import OrganizationTeacherUpsell from 'organizations/components/OrganizationTeacherUpsell';
import TitleHeader from 'components/TitleHeader';

import type { OrganizationUpsellPage as OrganizationUpsellPageQuery } from 'organizations/queries/__generated__/OrganizationUpsellPage';

import './styles/OrganizationUpsellPage.scss';

type Props = {
  data: ReactApolloData<OrganizationUpsellPageQuery>,
};

function ErrorView(props: { title: string, subtext: string }) {
  return (
    <CenteredContainer>
      <Box className="OrganizationUpsellPage-errorView">
        <TitleHeader
          title={props.title}
          subtext={props.subtext}
          image={require('assets/images/group-launch.svg')}
        />
      </Box>
    </CenteredContainer>
  );
}

export class OrganizationUpsellPage extends React.Component<Props> {
  render() {
    const { data } = this.props;

    if (data.loading) {
      return (
        <Container full>
          <LoadingIndicator align="center" />
        </Container>
      );
    }

    const organization = idx(data, _ => _.organizations[0]);

    if (!organization) {
      return (
        <ErrorReporter error="OrganizationUpsellPage - tried to load without an organization">
          <ErrorView
            title="Could not load this organization."
            subtext="Something went wrong, please come back later."
          />
        </ErrorReporter>
      );
    }

    const isPaid = idx(organization, _ => _.paid);
    const organizationUuid = idx(organization, _ => _.uuid);
    const admins = idx(organization, _ => _.admins) || [];
    const roles = idx(organization, _ => _.userAffiliation.roles) || [];
    const pendingRoles = idx(organization, _ => _.userAffiliation.pending_roles) || [];

    const isAdmin = roles.includes('admin');
    const isPendingAdmin = !isAdmin && !!pendingRoles.find(pr => pr && pr.role === 'admin');
    const isTeacher = !isAdmin && !isPendingAdmin && roles.includes('teacher');
    const orgHasVerifiedAdmin = admins.length > 0;

    if (isPaid && orgHasVerifiedAdmin) {
      return (
        <ErrorReporter error="OrganizationUpsellPage - paid orgs with verified admin should have a staff class">
          <ErrorView
            title="Could not load this class."
            subtext="This class is still being initialized, please come back later."
          />
        </ErrorReporter>
      );
    } else if (!isAdmin && !isPendingAdmin && !isTeacher) {
      return (
        <ErrorReporter error="OrganizationUpsellPage - student or parent accessed">
          <ErrorView
            title="Restricted page."
            subtext="You do not have permission to see this organization."
          />
        </ErrorReporter>
      );
    } else if (isAdmin || isPendingAdmin) {
      return (
        <div className="OrganizationUpsellPage-container">
          <GroupViewQueryParametersConsumer />
          <OrganizationAdminUpsell
            isPaid={isPaid}
            isPendingAdmin={isPendingAdmin}
            organizationUuid={organizationUuid}
            className="OrganizationUpsellPage"
          />
        </div>
      );
    } else {
      return (
        <div className="OrganizationUpsellPage-container">
          <GroupViewQueryParametersConsumer />
          <OrganizationTeacherUpsell
            organizationUuid={organizationUuid}
            className="OrganizationUpsellPage"
          />
        </div>
      );
    }
  }
}

const withOrganization = graphql(ORGANIZATION_UPSELL_PAGE_QUERY, {
  options: ({ params }) => ({ variables: { organizationId: params.id } }),
});

export default withOrganization(OrganizationUpsellPage);
